// Is this photo of a report good enough for the scan reader? Checked in the browser before upload, so a patient or navigator hears
// "retake it" at once instead of after a failed triage. Two measures: resolution, and sharpness (variance of the Laplacian on the
// greyscale image at a fixed width, so a large photo and a small one are judged alike). Calibrated on a blurry phone photo (score 18)
// against sharp report images (266 and up); the line sits well between them.
import { isScanImage } from './scanImage';

export const WORK_WIDTH = 1000;
export const MIN_SHARPNESS = 100;
export const MIN_LONG_SIDE = 1000;

/** Variance of the 4-neighbour Laplacian over the inside of a greyscale image (Uint8ClampedArray/array, row-major). */
export function laplacianVariance(gray, w, h) {
  if (w < 3 || h < 3) return 0;
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const l = gray[i - 1] + gray[i + 1] + gray[i - w] + gray[i + w] - 4 * gray[i];
      sum += l;
      sumSq += l * l;
      n++;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

/** Problems with a photo of the given size and sharpness: [] when it is fine. */
export function photoProblems({ width, height, sharpness }) {
  const problems = [];
  if (Math.max(width, height) < MIN_LONG_SIDE) {
    problems.push({ code: 'SMALL', message: `The photo is small (${width}×${height}px). Use at least ${MIN_LONG_SIDE}px on the long side.` });
  }
  if (sharpness != null && sharpness < MIN_SHARPNESS) {
    problems.push({ code: 'BLURRY', message: 'The photo looks blurry, so the numbers may not be readable. Retake it in focus, flat and well lit.' });
  }
  return problems;
}

/** The index of the sharpest of several frames ({sharpness}); the first one on a tie. -1 for none. */
export function pickSharpest(frames) {
  let best = -1;
  frames.forEach((f, i) => { if (best < 0 || f.sharpness > frames[best].sharpness) best = i; });
  return best;
}

/** Sharpness of a drawable (video frame, image, canvas) measured the same way as for an uploaded file: greyscale at a fixed width. */
export function sharpnessOf(source, width, height) {
  const w = WORK_WIDTH;
  const h = Math.max(3, Math.round(height * (w / width)));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const gray = new Float32Array(w * h);
  for (let i = 0; i < gray.length; i++) gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  return laplacianVariance(gray, w, h);
}

async function decode(file) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Checks an image file. Resolves to [] for anything that is not an image, or when the browser cannot decode it: never blocks an upload. */
export async function assessPhotoQuality(file) {
  if (!file || !isScanImage(file.type)) return [];
  try {
    const img = await decode(file);
    const width = img.width;
    const height = img.height;
    const sharpness = sharpnessOf(img, width, height);
    if (img.close) img.close();
    return photoProblems({ width, height, sharpness });
  } catch (e) {
    return [];
  }
}
